import { useEffect, useState } from "react";
import { SHARED_ME } from "./consts";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGetConsentNeighbor, useGetMyOwnedProperties } from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import moment from "moment";
import { Badge, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Mail } from "lucide-react";

const useMyNeighbors = () => {
  const [activeTab, setActiveTab] = useState<string>(SHARED_ME);
  const [propertyId, setPropertyId] = useState<string>("");
  const isMobile = useIsMobile();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);

  const callNeighborList = () => {
    if (propertyId && activeTab) {
      setTimeout(() => {
        refetchNeighborList();
      }, 100);
    }
  }

  const onTabChange = (value: string) => {
    setActiveTab(value);
    callNeighborList();
  }

  const { data: myProperties, isLoading: isLoadingProperties } = useGetMyOwnedProperties({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  useEffect(() => {
    if (myProperties && !propertyId) {
      setPropertyId(myProperties?.data?.[0]?.propertyId?.toString())
    }
  }, [myProperties]);

  const { data: neighborList, isLoading: isLoadingNeighborList, refetch: refetchNeighborList, isRefetching: isRefetchingNeighborList } = useGetConsentNeighbor({
    communityId: communityData?.communityId,
    propertyId,
    type: activeTab,
    queryParams: {
      enabled: !!(communityData?.communityId && propertyId && activeTab)
    }
  });

  const tab1Columns = [
    {
      accessorKey: "ownerName",
      header: <div>Owner Name</div>,
      accessorFn: (row: any) => String(row?.ownerName || ""),
    },
    {
      accessorKey: "propertyName",
      header: <div>Property Name</div>,
      accessorFn: (row: any) => String(`${row?.propertyName || ""} | ${row?.blockName || ""} | ${row?.zoneName || ""} | Unit ${row?.unitNumber || ""}` || ""),
    },
    {
      accessorKey: "email",
      header: <div>Email</div>,
      accessorFn: (row: any) => String(row?.email || ""),
    },
    {
      accessorKey: "mobile",
      header: <div>Mobile</div>,
      accessorFn: (row: any) => String(row?.mobile || ""),
    },
    {
      accessorKey: "sharedSince",
      header: <div>Shared Since</div>,
      cell: ({row}: any) => {
        return row?.original?.sharedSince ? moment(row?.original?.sharedSince).format("MM/DD/YYYY") : ""
      }, 
    },
    {
      accessorKey: "email",
      header: <></>,
      cell: ({row}: any) => {
        return (
          <Button variant="outline" onClick={() => window.open(`mailto:${row?.original?.email}`, "_blank")}>
            <Mail />
          </Button>
        )
      }, 
    },
  ];

  const tab2Columns = [
    {
      accessorKey: "currentOwnerName",
      header: <div>Owner Name</div>,
      accessorFn: (row: any) => String(row?.currentOwnerName || ""),
    },
    {
      accessorKey: "neighborPropertyName",
      header: <div>Property Name</div>,
      accessorFn: (row: any) => String(`${row?.neighborPropertyName || ""} | ${row?.neighborBlockName || ""} | ${row?.neighborZoneName || ""} | Unit ${row?.neighborUnitNumber || ""}` || ""),
    },
    {
      accessorKey: "neighborEmail",
      header: <div>Email</div>,
      accessorFn: (row: any) => String(row?.neighborEmail || ""),
    },
    {
      accessorKey: "neighborMobile",
      header: <div>Mobile</div>,
      accessorFn: (row: any) => String(row?.neighborMobile || ""),
    },
    {
      accessorKey: "effectiveFrom",
      header: <div>Shared Since</div>,
      cell: ({row}: any) => {
        return row?.original?.status?.toLowerCase() == "active" ? 
          row?.original?.effectiveFrom ? moment(row?.original?.effectiveFrom).format("MM/DD/YYYY") : "" : 
          <Badge
            variant="outline"
            className={cn(
              "rounded-full px-4 py-2 bg-red-50 text-red-600 border border-red-600"
            )}
          >
            Inactive
          </Badge>
      }, 
    },
  ];

  const onChangeProperty = (value: string) => {
    setPropertyId(value);
    callNeighborList();
  }

  return {
    activeTab,
    onTabChange,
    isMobile,
    isLoadingNeighborList: isLoadingNeighborList || isRefetchingNeighborList,
    neighborList: neighborList?.data || [],
    columns: activeTab == SHARED_ME ? tab1Columns : tab2Columns,
    propertyId,
    setPropertyId,
    isLoadingProperties,
    myProperties: myProperties?.data || [],
    onChangeProperty
  }
}

export default useMyNeighbors;